import { authResource, AuthResource } from '@witkac/kartamiejska-resources';
import { Login, Profile, ProfileType, SessionStorage } from '@witkac/kartamiejska-shared';
import jwtDecode from 'jwt-decode';

import { AbstractModule } from './abstract.module';

/**
 * Moduł autentykacji
 *
 * @class AuthModule
 * @extends {AbstractModule<AuthResource>}
 */
class AuthModule extends AbstractModule<AuthResource> {
  public state = {
    profile: undefined,
    permissions: {},
    refreshId: false,
    pendingMfaLogin: null
  };

  public mutations = {
    /**
     * Zapisuje dane profilu do store
     *
     * @param localState state
     * @param profile profil użytkownika
     */
    save(localState: any, profile: Profile): void {
      localState.profile = profile;
    },
    /**
     * Zapisuje uprawnienia do  store
     *
     * @param {*} localState
     * @param {object} permission
     */
    savePermissions(localState: any, permissions: any): void {
      localState.permissions = { ...localState.permissions, ...permissions };
    },

    /**
     * Ustawia id timeoutu odświeżającego token
     *
     * @param {*} localState
     * @param {boolean} refreshId
     */
    setRefreshId(localState: any, refreshId: boolean): void {
      localState.refreshId = refreshId;
    },

    setPendingMfaLogin(localState: any, payload: { login: string; password: string } | null): void {
      localState.pendingMfaLogin = payload;
    },

    /**
     * Wylogowuje użytkownika
     *
     * @param {*} localState
     */
    unauthorize(localState: any) {
      if (localState.refreshId) {
        clearTimeout(localState.refreshId);
        localState.refreshId = null;
      }

      localStorage.clear();
    }
  };

  public actions = {
    completeLogin: (
      context: any,
      data: { token: string; profile: Profile; hasAllConsents: boolean }
    ): { profile: Profile; hasAllConsents: boolean } => {
      const { token, profile, hasAllConsents } = data;

      if (profile.type === ProfileType.CLIENT) {
        throw new Error('Brak uprawnień. Należy użyć aplikacji przeznaczonej dla mieszkańców.');
      }

      if (profile.partner) {
        localStorage.setItem(SessionStorage.PARTNER_ID, '' + profile.partner.id);
      }
      context.commit('setPendingMfaLogin', null);
      localStorage.setItem(SessionStorage.TOKEN, token);
      localStorage.setItem(SessionStorage.PROFILE_ID, '' + profile.id);
      localStorage.setItem(SessionStorage.USER_ID, '' + profile.userId);
      localStorage.setItem(SessionStorage.PROFILE_TYPE, '' + profile.type);
      context.commit('save', profile);
      context.dispatch('updateTokenOnExpire', token);
      return { profile, hasAllConsents };
    },

    /**
     * Logowanie
     *
     * @param context context
     * @param data dane logowania
     */
    login: (context: any, data: Login): Promise<{ profile: Profile; hasAllConsents: boolean }> =>
      (this.resource as any)
        .login(data.login, data.password)
        .then((response: { requiresMfa: boolean; token?: string; profile?: Profile; hasAllConsents?: boolean }) => {
          if (response.requiresMfa) {
            context.commit('setPendingMfaLogin', { login: data.login, password: data.password });
            return { requiresMfa: true } as any;
          }

          return context.dispatch('completeLogin', {
            token: response.token,
            profile: response.profile,
            hasAllConsents: response.hasAllConsents
          });
        }),

    setPendingMfaLogin: (context: any, payload: { login: string; password: string } | null): void => {
      context.commit('setPendingMfaLogin', payload);
    },

    requestLoginMfa: (
      context: any,
      data: Login & { rememberMe?: boolean }
    ): Promise<{ expiresAt: Date; resendAvailableAt: Date }> =>
      (this.resource as any).requestLoginMfa(data.login, data.password, data.rememberMe),

    verifyLoginMfa: (
      context: any,
      data: Login & { code: string; rememberMe?: boolean }
    ): Promise<{ profile: Profile; hasAllConsents: boolean }> =>
      (this.resource as any)
        .verifyLoginMfa(data.login, data.password, data.code, data.rememberMe)
        .then((response: { requiresMfa: boolean; token?: string; profile?: Profile; hasAllConsents?: boolean }) =>
          context.dispatch('completeLogin', {
            token: response.token,
            profile: response.profile,
            hasAllConsents: response.hasAllConsents
          })
        ),

    getMfaStatus: (context: any, userId: string): Promise<boolean> => (this.resource as any).profileMfaEnabled(userId),

    requestEnableMfa: (
      context: any,
      data: { userId: string; password: string }
    ): Promise<{ expiresAt: Date; resendAvailableAt: Date }> =>
      (this.resource as any).requestEnableMfa(data.userId, data.password),

    enableMfa: (context: any, data: { userId: string; code: string }): Promise<boolean> =>
      (this.resource as any).enableMfa(data.userId, data.code),

    requestDisableMfa: (context: any, userId: string): Promise<{ expiresAt: Date; resendAvailableAt: Date }> =>
      (this.resource as any).requestDisableMfa(userId),

    disableMfa: (context: any, data: { userId: string; code: string }): Promise<boolean> =>
      (this.resource as any).disableMfa(data.userId, data.code),

    /**
     * Odświeża token
     *
     * @type {async}
     * @memberof AuthModule
     */
    refreshToken: (context: any): Promise<void> =>
      this.resource
        .refreshToken()
        .then(token => {
          localStorage.setItem(SessionStorage.TOKEN, token);
          context.commit('setRefreshId', null);
          context.dispatch('updateTokenOnExpire', token);
        })
        .catch(() => context.commit('setRefreshId', null)),
    /**
     * Odświeża token, gdy kończy się jego ważność
     */
    updateTokenOnExpire: (context: any, token: string): void => {
      token = token || (localStorage.getItem(SessionStorage.TOKEN) as string);
      if (context.state.refreshId || !token) {
        return;
      }
      const tokenPayload: any = jwtDecode(token);
      if (tokenPayload.exp) {
        const tokenExpInMs = tokenPayload.exp * 1000;
        const reservedTime = 1000 * 60 * 6;
        const timeToExpiration = tokenExpInMs - new Date().getTime() - reservedTime;
        const timeoutId = setTimeout(() => context.dispatch('refreshToken'), timeToExpiration);
        context.commit('setRefreshId', timeoutId);
      }
    },
    /**
     * Sprawdzanie uprawnień
     *
     * @param context context
     * @param permission dane
     *
     */
    checkPermissions: async (context: any, permissionsNames: []): Promise<void> =>
      this.resource.checkPermission(permissionsNames).then(valid => {
        const permissions: any = {};
        for (let i = 0; i < permissionsNames.length; i++) {
          permissions[permissionsNames[i]] = valid[i];
        }
        context.commit('savePermissions', permissions);
      }),
    /**
     * Zmiana hasła
     *
     * @param {*} context
     * @param {*} variables
     * @returns {Promise<boolean>}
     */
    changePassword: (context: any, variables: any): Promise<boolean> => this.resource.changePassword(variables),

    /**
     * Ręczne potwierdzenie konta
     *
     * @param {*} context
     * @param {{ userId: string; password: string }} data
     * @returns {Promise<number>}
     */
    confirmManually: (context: any, data: { userId: string; password: string }): Promise<number> =>
      this.resource.confirmManually(data.userId, data.password)
  };
}

export default new AuthModule(authResource);
